<template>
  <div class="attachment-field">
    <div v-if="!value" @click="openFilePicker()" class="attachment-box is-empty">
      <div class="file-name">{{ placeholder }}</div>

      <div class="file-actions">
        <b-spinner v-if="isReading" small />

        <i v-else class="icon-clip" />
      </div>
    </div>

    <div v-else class="attachment-box">
      <div class="file-name">{{ value.name }}</div>

      <div class="file-actions">
        <b-link v-if="value.url" :href="value.url" class="file-action" target="_blank" title="Visualizar">
          <i class="icon-eye" />
        </b-link>

        <div @click="openFilePicker()" class="file-action" title="Trocar arquivo">
          <b-spinner v-if="isReading" small />

          <i v-else class="icon-pencil-line" />
        </div>

        <div @click="removeAttachment()" class="file-action remove" title="Remover arquivo">
          <i class="icon-trash" />
        </div>
      </div>

      <label class="field-label">{{ label }}</label>
    </div>

    <small class="attachment-hint">
      {{ isPending ? "Será enviado ao salvar." : hint }}
    </small>

    <input
      @change="handleSelection($event)"
      ref="fileInput"
      :accept="accept"
      type="file"
      class="hidden-input"
    />
  </div>
</template>

<script>
import { BLink, BSpinner } from "bootstrap-vue";

import {
  UPLOAD_MAX_SIZE_LABEL,
  readUploadAsBase64,
  validateUpload,
} from "@/services/uploadValidationService.js";

export default {
  name: "AttachmentField",

  components: {
    BLink,
    BSpinner,
  },

  props: {
    value: {
      type: Object,
      required: false,
      default: null,
    },

    label: {
      type: String,
      required: false,
      default: "Anexo",
    },

    placeholder: {
      type: String,
      required: false,
      default: "Anexar arquivo",
    },

    accept: {
      type: String,
      required: false,
      default: null,
    },

    allowedMimeTypes: {
      type: Array,
      required: false,
      default: null,
    },

    hint: {
      type: String,
      required: false,
      default: `Qualquer tipo de arquivo, até ${UPLOAD_MAX_SIZE_LABEL}.`,
    },
  },

  data() {
    return {
      isReading: false,
    };
  },

  computed: {
    isPending() {
      return Boolean(this.value?.base64File);
    },
  },

  methods: {
    openFilePicker() {
      this.$refs.fileInput.click();
    },

    removeAttachment() {
      this.$emit("input", null);
    },

    showValidationError(reason) {
      this.$bvToast.toast(reason, {
        title: "Arquivo não suportado",
        autoHideDelay: 5000,
        variant: "danger",
        toaster: "b-toaster-top-left",
        solid: true,
      });
    },

    async handleSelection(event) {
      const file = event.target.files[0];

      event.target.value = "";

      if (!file) return;

      const validation = validateUpload(file, this.allowedMimeTypes);

      if (!validation.valid) {
        this.showValidationError(validation.reason);

        return;
      }

      this.isReading = true;

      try {
        const attachment = await readUploadAsBase64(file);

        this.$emit("input", attachment);
      } catch (error) {
        this.showValidationError(`Não foi possível ler o arquivo "${file.name}".`);
      } finally {
        this.isReading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.attachment-field {
  margin-bottom: 1rem;
  width: 100%;

  .attachment-box {
    display: flex;
    align-items: center;
    position: relative;
    height: 45px;
    width: 100%;
    padding: 0 14px 0 20px;
    gap: 10px;
    border: 1px solid #d2d2d2;
    border-radius: 5px;
    background: #ffffff;
    color: #4c5862;
    font-size: 14px;

    &.is-empty {
      cursor: pointer;

      .file-actions i {
        color: #8e8e8e;
        font-size: 22px;
      }
    }

    .file-name {
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .file-actions {
      display: flex;
      align-items: center;
      gap: 12px;

      .file-action {
        display: flex;
        align-items: center;
        color: #046af3;
        font-size: 20px;
        cursor: pointer;

        &.remove {
          color: #ea5455;
        }
      }
    }

    .field-label {
      position: absolute;
      top: -11px;
      left: 16px;
      padding: 0 5px;
      background: #ffffff;
      color: #999999;
      font-size: 14px;
      font-weight: 400;
      margin-bottom: 0;
      pointer-events: none;
      user-select: none;
    }
  }

  .attachment-hint {
    display: block;
    margin-top: 4px;
    color: #7d8797;
    font-size: 11px;
  }

  .hidden-input {
    display: none;
  }
}
</style>
